import * as Sentry from '@sentry/browser';
import isDeepEqual from 'fast-deep-equal/react';
import { pickBy } from 'lodash';
import { DependencyList, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { logout } from '../../store/authentication/actions';

export type PromiseReturnType<T extends (args: any) => any> = ReturnType<T> extends Promise<infer U> ? U : never;

export function useReactAsync<Args extends object, Result extends (a: Args) => Promise<any>>(
    promiseFn: Result,
    args: Args,
    deps?: DependencyList,
): {
    data: PromiseReturnType<Result> | undefined;
    isLoading: boolean;
    reload: () => Promise<void>;
    error: Error | undefined;
} {
    const [data, setData] = useState<PromiseReturnType<Result> | undefined>(undefined);
    const [isLoading, setIsLoading] = useState(false);
    const isMounted = useRef(true);
    const [error, setError] = useState<Error | undefined>(undefined);
    const dispatch = useDispatch();

    // `args` can be an object, so we need to compare them deeply not by reference
    const argsRef = useRef(args);
    if (
        !isDeepEqual(
            pickBy(argsRef.current, (v) => v !== undefined),
            pickBy(args, (v) => v !== undefined),
        )
    ) {
        argsRef.current = args;
    }

    const getData = useCallback(async () => {
        setIsLoading(true);
        try {
            const newData = (await promiseFn(argsRef.current)) as PromiseReturnType<Result>;
            if (isMounted.current) {
                setData(newData);
                setIsLoading(false);
            }
        } catch (e) {
            if ('statusCode' in e && e.statusCode === 401) {
                dispatch(logout() as any);
            } else {
                Sentry.captureException(e);
                setError(e);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- Rule does not understand argsRef.current
    }, [promiseFn, argsRef.current]);

    // We need to stringify the deps to make sure that the useEffect hook is called when the deps change
    const depsAsString = deps ? JSON.stringify(deps) : '';

    useEffect(() => {
        void getData();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- Deps need to be deconstructed, but can be undefined
    }, [getData, depsAsString]);

    useEffect(
        () => () => {
            isMounted.current = false;
        },
        [],
    );

    async function reload() {
        await getData();
    }
    return { data, isLoading, reload, error };
}
